import React, { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client/react";

import { FETCH_PROJECT_QUERY } from "../constants/graphql";
import useAuth from "../hooks/useAuth";

const CustomFieldsContext = React.createContext(null);

const initialState = {
  id: true,
  asset_id: true,
  barcode: true,
  barcode_number: true,
  color: true,
  condition: true,
  description: true,
  description_comments: true,
  destination_comments: true,
  destination_employee_name: true,
  destination_quantity: true,
  destination_room_number: true,
  dimensions: true,
  disposition: true,
  existing_building: true,
  existing_floor: true,
  existing_room: true,
  finishes: true,
  manufacturer: true,
  new_building: true,
  new_floor: true,
  new_room: true,
  origin_comments: true,
  origin_cubby_number: true,
  origin_employee_name: true,
  origin_room_number: true,
  photo: true,
  qr_code_image: true,
  quantity: true,
};

export const CustomFieldsProvider = ({ children }) => {
  const customFields = localStorage.getItem("customFields");
  const [state, setState] = useState(customFields ? JSON.parse(customFields) : initialState);

  const handleChange = value => {
    localStorage.setItem("customFields", JSON.stringify(value));
    setState(value);
  };

  return (
    <CustomFieldsContext.Provider value={[state, handleChange]}>
      {children}
    </CustomFieldsContext.Provider>
  );
};

export const fetchSettings = (project, key = "fieldSettings") => {
  if (!project) return;

  return project[key];
};

export const useCustomFields = (props = {}, key = "fieldSettings") => {
  const { user } = useAuth();
  const { company } = user;
  const params = useParams();
  const projectId = props.projectId || params.projectId;

  const { data: { project = {} } = {}, loading } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const customFields = useMemo(() => {
    return fetchSettings(project, key) || company.fieldSettings;
  }, [project, company, loading]);

  const value = useMemo(() => {
    return [customFields, loading];
  }, [customFields, projectId, loading]);

  return value;
};

export const useFieldSettings = (project, key = "fieldSettings") => {
  const { user } = useAuth();
  const { company } = user;

  return fetchSettings(project, key) || company.fieldSettings;
};

export default CustomFieldsContext;
